import {
  createContext,
  type PropsWithChildren,
  useContext,
  useEffect,
  useMemo,
  useState
} from 'react';
import { useWidgetTokenStore } from '@elfsight/lib-utils';
import { useSettings } from '@wu/widget-core';
import { shuffle } from 'es-toolkit';
import { getMaxSize, getUpdatedLogos } from '../helpers';

const LogoContext = createContext({});

export const LogoProvider = ({ children }: PropsWithChildren) => {
  const { items, randomOrder } = useSettings();
  const widgetTokenStore = useWidgetTokenStore();
  const [processedLogos, setProcessedLogos] = useState({});

  const logoOrders = useMemo(() => {
    const orders = items.map((_, i) => i);

    return randomOrder ? shuffle(orders) : orders;
  }, [randomOrder, items.length]);

  useEffect(() => {
    const processLogosWrapper = async () => {
      const widgetToken = widgetTokenStore.get();

      if (!widgetToken) {
        return;
      }

      try {
        const { newProcessedLogos, logosToProcess } = items.reduce(
          (settings, item, index) => {
            const { aspectRatio, ...otherSettings } =
              processedLogos[item.id] || {};
            if (item.file?.data?.url === otherSettings.file?.data?.url) {
              settings.newProcessedLogos[item.id] = {
                ...otherSettings,
                ...item,
                aspectRatio
              };
            } else {
              settings.logosToProcess.push({ item, index });
            }

            return settings;
          },
          { newProcessedLogos: {}, logosToProcess: [] }
        );

        if (logosToProcess.length) {
          const itemsToProcess = logosToProcess.map(({ item }) => item);
          const updatedLogos = await getUpdatedLogos(
            itemsToProcess,
            widgetToken
          );

          updatedLogos.forEach((logo, i) => {
            const { index } = logosToProcess[i];
            const item = items[index];

            newProcessedLogos[item.id] = {
              ...logo
            };
          });
        }

        setProcessedLogos(newProcessedLogos);
      } catch (e) {
        console.warn(e);
      }
    };

    processLogosWrapper();
  }, [items, widgetTokenStore]);

  const value = useMemo(() => {
    const logos = items.map((item) => processedLogos[item.id]).filter(Boolean);
    const maxSize = getMaxSize(logos);

    return logos
      .map((logo, index) => ({
        ...logo,
        maxSize,
        order: logoOrders[index]
      }))
      .sort((a, b) => a.order - b.order);
  }, [items, processedLogos, logoOrders]);

  return <LogoContext.Provider value={value}>{children}</LogoContext.Provider>;
};

export const useLogos = () => useContext(LogoContext);
